<template>
  <div>
    <!-- Toolbar -->
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search history..."
      :total="total"
      :page="page"
      :limit="perPage"
      :active-filter-count="activeFilterCount"
      :sort-options="[]"
      @reload="loadHistory"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Status</label>
          <select v-model="filterStatus">
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Event</label>
          <select v-model="filterEvent">
            <option value="">All</option>
            <option value="schedule.started">Schedule Started</option>
            <option value="schedule.ended">Schedule Ended</option>
          </select>
        </div>
      </template>
      <template #actions>
        <router-link :to="{ name: 'webhooks' }" class="btn-secondary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </router-link>
      </template>
    </ListToolbar>

    <!-- Webhook info -->
    <div v-if="webhookName || webhookUrl" class="flex items-center gap-3 mb-4">
      <h2 class="text-lg font-semibold text-gray-900">{{ webhookName }}</h2>
      <span v-if="webhookUrl" class="text-sm text-gray-400 font-data">{{ webhookUrl }}</span>
    </div>

    <!-- Content -->
    <div class="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
      <!-- List -->
      <div class="bg-white rounded-xl border border-gray-200/60 overflow-hidden card-shadow">
        <!-- Loading skeleton -->
        <div v-if="loading">
          <div v-for="i in 6" :key="'skel-' + i" class="flex items-center gap-3 px-6 py-4 border-b border-gray-50">
            <div class="w-2 h-2 rounded-full bg-gray-100 skeleton-pulse shrink-0" />
            <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (30 + i * 8) + '%' }" />
            <div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-24 ml-auto" />
          </div>
        </div>

        <!-- Items -->
        <div v-else-if="items.length" class="divide-y divide-gray-50">
          <div
            v-for="entry in items" :key="entry._id"
            @click="selectedItem = entry"
            :class="['flex items-center justify-between px-6 py-4 cursor-pointer transition-colors', selectedItem?._id === entry._id ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50/50']"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span :class="['w-2 h-2 rounded-full shrink-0', statusDotClass(entry.status)]" />
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ entry.request?.type || '—' }}</p>
                <p class="text-sm text-gray-500">{{ $filters.formatDatetime(entry.created_at) }}</p>
              </div>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <span class="text-sm font-data text-gray-500 hidden sm:inline">{{ entry.resource_id }}</span>
              <span v-if="entry.status_code" class="text-sm font-data" :class="entry.status_code >= 400 ? 'text-red-500' : 'text-green-600'">{{ entry.status_code }}</span>
              <span :class="statusBadgeClass(entry.status)">{{ entry.status }}</span>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <div v-else class="px-6 py-20 text-center">
          <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <p class="text-base font-medium text-gray-900">No history yet</p>
          <p class="text-sm text-gray-500 mt-1">Webhook deliveries will appear here</p>
        </div>
      </div>

      <!-- Detail panel -->
      <div v-if="selectedItem" class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden self-start sticky top-4">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 class="text-[15px] font-semibold text-gray-900">Detail</h3>
            <p class="text-sm text-gray-400 mt-0.5">{{ $filters.formatDatetime(selectedItem.created_at) }}</p>
          </div>
          <div class="flex items-center gap-2">
            <span v-if="selectedItem.status_code" class="text-sm font-data font-medium" :class="selectedItem.status_code >= 400 ? 'text-red-500' : 'text-green-600'">{{ selectedItem.status_code }}</span>
            <span :class="statusBadgeClass(selectedItem.status)">{{ selectedItem.status }}</span>
          </div>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <p class="text-sm font-medium text-gray-600 mb-1.5">Request</p>
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 overflow-y-auto max-h-60 space-y-1.5" v-html="renderObject(selectedItem.request)" />
          </div>
          <div>
            <p class="text-sm font-medium text-gray-600 mb-1.5">Response</p>
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 overflow-y-auto max-h-60 space-y-1.5" v-html="renderObject(selectedItem.response)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../../services/api.js'
import ListToolbar from '../../components/ListToolbar.vue'

export default {
  name: 'WebhookHistory',
  components: { ListToolbar },

  data() {
    return {
      loading: true,
      webhookId: null,
      webhookName: '',
      webhookUrl: '',
      items: [],
      selectedItem: null,
      search: '',
      filterStatus: '',
      filterEvent: '',
      page: 1,
      perPage: 50,
      total: 0
    }
  },

  computed: {
    activeFilterCount() {
      return (this.filterStatus ? 1 : 0) + (this.filterEvent ? 1 : 0)
    }
  },

  watch: {
    search() {
      this.page = 1
      this.loadHistory()
    },
    filterStatus() {
      this.page = 1
      this.loadHistory()
    },
    filterEvent() {
      this.page = 1
      this.loadHistory()
    }
  },

  async mounted() {
    this.webhookId = this.$route.params.id
    await this.loadWebhook()
    await this.loadHistory()
  },

  methods: {
    async loadWebhook() {
      try {
        const res = await api.get(`/webhooks/${this.webhookId}`, { silent: true })
        const wh = res.data?.webhook || res.data || {}
        this.webhookName = wh.name || ''
        this.webhookUrl = wh.url || ''
      } catch { /* silent */ }
    },

    async loadHistory() {
      this.loading = true
      try {
        const params = { limit: this.perPage, page: this.page, count: 1 }
        if (this.search) params.text = this.search
        if (this.filterStatus) params.status = this.filterStatus
        if (this.filterEvent) params.event = this.filterEvent
        const res = await api.get(`/webhooks/${this.webhookId}/history`, { params, silent: true })
        this.items = res.data?.items || []
        this.total = res.data?.total || 0
        if (this.items.length) this.selectedItem = this.items[0]
        else this.selectedItem = null
      } catch { /* silent */ } finally { this.loading = false }
    },

    goToPage(p) {
      if (p < 1 || p === this.page) return
      this.page = p
      this.selectedItem = null
      this.loadHistory()
    },

    clearFilters() {
      this.filterStatus = ''
      this.filterEvent = ''
    },

    statusDotClass(status) {
      if (status === 'success') return 'bg-green-500'
      if (status === 'failed') return 'bg-red-500'
      return 'bg-gray-300'
    },

    statusBadgeClass(status) {
      if (status === 'success') return 'badge-live'
      if (status === 'failed') return 'badge-error'
      return 'badge-idle'
    },

    renderObject(obj, depth = 0) {
      if (obj === null || obj === undefined) return '<span class="text-sm text-gray-400 italic">null</span>'
      if (typeof obj === 'string') return `<span class="text-sm font-data text-green-700">"${this.escapeHtml(obj)}"</span>`
      if (typeof obj === 'number' || typeof obj === 'boolean') return `<span class="text-sm font-data text-blue-600">${obj}</span>`
      if (Array.isArray(obj)) {
        if (!obj.length) return '<span class="text-sm text-gray-400">[]</span>'
        const items = obj.map(v => `<div style="padding-left:${(depth + 1) * 16}px" class="flex items-start gap-1.5"><span class="text-gray-300 mt-0.5">•</span>${this.renderObject(v, depth + 1)}</div>`).join('')
        return items
      }
      if (typeof obj === 'object') {
        const keys = Object.keys(obj)
        if (!keys.length) return '<span class="text-sm text-gray-400">{}</span>'
        return keys.map(k => {
          const val = obj[k]
          const isNested = val && typeof val === 'object'
          if (isNested) {
            return `<div style="padding-left:${depth * 16}px"><span class="text-sm font-medium text-gray-500">${this.escapeHtml(k)}:</span></div>${this.renderObject(val, depth + 1)}`
          }
          return `<div style="padding-left:${depth * 16}px" class="flex items-baseline gap-2"><span class="text-sm font-medium text-gray-500">${this.escapeHtml(k)}:</span>${this.renderObject(val, depth + 1)}</div>`
        }).join('')
      }
      return `<span class="text-sm font-data text-gray-700">${this.escapeHtml(String(obj))}</span>`
    },

    escapeHtml(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    }
  }
}
</script>
